import React, { useCallback, useEffect, useState } from "react";
import { motion, type PanInfo } from "framer-motion";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";
import { heroSlides } from "../../data/site";
import { IntroSlide } from "./hero/IntroSlide";
import { ProductSlide } from "./hero/ProductSlide";
const ease = [0.23, 1, 0.32, 1] as const;
const SLIDE_MS = 8000;
const count = heroSlides.length + 1;
export function Hero() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = useCallback((dir: number) => setIndex(i => (i + dir + count) % count), []);
  useEffect(() => {
    if (paused) return;
    const t = window.setTimeout(() => go(1), SLIDE_MS);
    return () => window.clearTimeout(t);
  }, [index, paused, go]);
  const onPanEnd = (_e: PointerEvent | MouseEvent | TouchEvent, info: PanInfo) => {
    if (info.offset.x < -60) go(1);else if (info.offset.x > 60) go(-1);
  };
  const slides = [{
    id: "intro",
    render: (active: boolean) => <IntroSlide active={active} data-id="element-354" data-mp="3jibg2ahyfjp2" />
  }, ...heroSlides.map(s => ({
    id: s.id,
    render: (active: boolean) => <ProductSlide slide={s} active={active} data-id="element-355" data-mp="3nhsnvakussxe" />
  }))];
  return <section id="top" aria-roledescription="carousel" aria-label="Highlights"
  // On desktop the header is exactly one screen tall, so no slide forces extra empty space onto the others.
  className="relative flex min-h-[100svh] flex-col overflow-hidden bg-sun pt-24 md:pt-28 lg:h-[100svh] lg:min-h-[680px]" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} data-id="element-356" data-mp="39rp54tl6nhcp">
      {/* min-h-0 lets this column shrink to the screen height so the arrow bar is never pushed out of view. */}
      <div className="relative mx-auto flex w-full max-w-[1400px] flex-1 flex-col px-5 md:px-8 lg:min-h-0" data-id="element-357" data-mp="p0ygeff79f3v">
        <motion.div className="grid flex-1 lg:min-h-0 lg:grid-rows-[minmax(0,1fr)]" onPanEnd={onPanEnd} style={{
        touchAction: "pan-y"
      }} data-id="element-358" data-mp="tz9q8q5hvpm3">
          {slides.map((s, i) => {
          const isActive = i === index;
          return <motion.div key={s.id} role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${count}`} aria-hidden={!isActive} className={`col-start-1 row-start-1 lg:min-h-0 lg:overflow-hidden ${isActive ? "z-10" : "pointer-events-none"}`} initial={false} animate={{
            opacity: isActive ? 1 : 0
          }} transition={{
            duration: 0.3,
            ease
          }} data-id="element-359" data-mp="nm5ijbddpox7" data-mpi={i}>
                {s.render(isActive)}
              </motion.div>;
        })}
        </motion.div>

        <div className="relative z-10 flex shrink-0 items-center justify-end gap-3 border-t-2 border-ink py-4" data-id="element-360" data-mp="wiwk23ap9vz6">
          <button type="button" onClick={() => go(-1)} aria-label="Previous slide" className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-ink transition-colors duration-150 hover:bg-ink hover:text-sun" data-id="element-361" data-mp="1p9og90rrmkmi">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-362" data-mp="26iqqazyvle11" />
          </button>
          <div className="flex items-center gap-2 px-1" data-id="element-363" data-mp="26hfjsse1ps68">
            {slides.map((s, i) => <button key={s.id} type="button" onClick={() => setIndex(i)} aria-label={`Go to slide ${i + 1}`} aria-current={i === index ? "true" : undefined} className="py-3" data-id="element-364" data-mp="2bj4yci0h8nz2" data-mpi={i}>
                <span className="block h-1.5 w-10 overflow-hidden rounded-full bg-ink/20 md:w-14" data-id="element-365" data-mp="2p9qjkddela04">
                  {i === index && (paused ? <span className="block h-full w-full rounded-full bg-ink" data-id="element-366" data-mp="2z87cvgzte0pc" /> : <motion.span key={index} className="block h-full rounded-full bg-ink" initial={{
                width: "0%"
              }} animate={{
                width: "100%"
              }} transition={{
                duration: SLIDE_MS / 1000,
                ease: "linear"
              }} data-id="element-367" data-mp="33a1bljqg8umv" />)}
                </span>
              </button>)}
          </div>
          <span className="w-12 text-center text-[12px] font-bold tabular-nums" data-id="element-368" data-mp="9lba7ktma7s7">
            {String(index + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
          </span>
          <button type="button" onClick={() => go(1)} aria-label="Next slide" className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-ink transition-colors duration-150 hover:bg-ink hover:text-sun" data-id="element-369" data-mp="3d1i2kv6l0ilo">
            <ArrowRightIcon className="h-5 w-5" data-id="element-370" data-mp="16865lsebr07z" />
          </button>
        </div>
      </div>
    </section>;
}